// §4.14 THE BOARD, /sales/pipeline (Module 09, P3-E2 PR E2-1; D-244, D-312).
//
// ONE READ. `GET /v1/opportunities/board` answers every column at once: the
// stage, the whole stage's count and rotting count, a value reader's totals
// per currency, and the first 25 cards in board order. The column head prints
// the SERVER's count and totals for the whole stage, never a sum of the cards
// this page has loaded, so a column with more deals than it shows still reads
// true (spec 09 §4.14). Show more reads the next page of the deal list for
// that stage with the board's filters and the column's own cursor.
//
// MOVING A DEAL. Three ways, one outcome:
//   * a pointer drags a card (6 px before a press becomes a drag, pointer
//     capture, the card stays in its column as the placeholder and a ghost
//     follows the pointer by `transform`, the board scrolls at its edges,
//     `pointercancel` or Escape cancels, and a touch never starts a drag);
//   * the keyboard picks a card up with Space, chooses with the arrows, drops
//     with Space or Enter and cancels with Escape, and a polite live region
//     says each step;
//   * every card's Move button opens the same choice as a dialog, the one way
//     on a phone.
// Every drop sends one request naming the stage the card left. The card moves
// at once and shows that it is saving; a refused drop puts it back in the
// column and at the index it was picked from and says why in one line
// (`role="alert"`), and the board then reads the server again, so a card
// someone else moved ends where the deal really is. A move that never reached
// the server says so and offers Try again, which sends the identical request
// with the same Idempotency-Key. A drop on Lost sends nothing until the reason
// dialog confirms. The rules are `lib/board-state.ts`'s pure reducer.
//
// ESCAPE BELONGS TO THE DRAG FIRST. The side peek closes on an Escape heard
// in the bubble phase unless the event is already consumed, so the board's
// cancel listens in the CAPTURE phase and calls `preventDefault` (WP-E's
// convention). Two bubble listeners on `document` run in the order they were
// added, and the peek's is always the older one, so a bubble-phase cancel
// would hear the key after the peek had already closed. No Modal is open
// while a card is moving, so nothing else wants the key first.
//
// THE CLICK A DRAG ENDS WITH. A pointer drag ends with the browser's click on
// whatever lies under both the press and the release, and that click is not
// the reader choosing a control. The board swallows it once. A drop re-parents
// the card into another column, so that click usually never reaches the board
// at all, and a flag waiting for it would eat the reader's next real press
// instead. The flag therefore lives for the rest of the release's task only
// (a zero-delay timer clears it) and any new press clears it too.
//
// THE PRESS IS THE BOARD'S, NOT THE BROWSER'S. A mouse press on a card that
// can move belongs to the board until the button comes up. The browser would
// otherwise select the text the pointer passes over, and a later press on
// that selected text is the browser's own drag of it: it fires `dragstart`,
// then `pointercancel`, and sends no more moves, so the board's 6 px never
// comes and the card cannot be picked up again. M09-S22 step 6 met exactly
// that on staging, when the drag cancelled with Escape left its text
// selected and the second drag of the same card never started. Chromium on
// macOS holds a text drag back for the first 150 ms of a press and Linux and
// Windows do not, so a quick press on a Mac hid it. So the press clears
// whatever text is selected, and until the release `selectstart` and
// `dragstart` are refused, which keeps the pointer stream the board's. A
// touch, a press on a card that cannot move and a press on a card's own
// control are left to the browser (spec 09 §4.14, "Moving a deal").
//
// ONE REVEAL FOR AN ADMIN. An admin's owner filter lists the member
// directory, so the filter row and the columns wait for it as well as for the
// board: painted first, the Select widened when the names landed and pushed
// the controls beside it (design-system §5).
//
// NO SAVED VIEWS (D-312 ruling 20). The board carries the owner filter (for
// admins) and the customer picker, under the list's own keys, and the tab
// strip carries both to the list and the forecast.
//
// NEW DEAL FROM THE ADDRESS. `new_deal=<customer uuid>` opens New deal with
// that customer locked, and `from_conversation=<uuid>` beside it shows the
// read-only "From the conversation" line and records the thread on the deal.
// Both leave the address with a replace when the dialog saves or closes, so
// Back never reopens it (spec 09 §4.14; module 17's Create deal door).
//
// PHONE. Below 769 px a row of stage chips selects one stage and its cards
// stack under it; Move is the way to every stage (no drag on a phone).

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { useOnboardingPermissions } from "../../../onboarding/hooks/permissions";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  Skeleton,
} from "../../../../components/states";
import { useDesktop } from "../../../../components/use-desktop";
import { peekCode } from "../../../../components/peek-address";
import { UnauthorizedError } from "../../../../lib/problem";
import { newIdempotencyKey, RequestError, requestIdOf } from "../../../../lib/request";
import {
  boardReducer,
  INITIAL_BOARD_STATE,
  placeCards,
  sameTarget,
  type CardOrigin,
  type DropTarget,
  type PendingMove,
} from "../lib/board-state";
import { formatMinor } from "../lib/money";
import { definedOnly, refusalText, stageName } from "../lib/pipeline";
import { MutationFailureNote } from "../mutation-failure-note";
import {
  canWriteOpportunities,
  isSalesReadRefused,
  useSalesPermissions,
} from "../hooks/permissions";
import { useOwnerIndex } from "../hooks/sales-queries";
import {
  BOARD_PAGE_SIZE,
  fetchOpportunitiesPage,
  isNetworkFailure,
  useLoseOpportunity,
  useMoveOpportunity,
  useOpportunityBoard,
  useWinOpportunity,
  type Opportunity,
  type OpportunityBoardColumn,
  type PipelineStage,
} from "../hooks/opportunity-queries";
import { NewTaskModal } from "../components/sales-tasks";
import { BoardCard, isInteractive } from "../components/pipeline/board-card";
import { LoseDealModal, MoveDealModal, NewDealModal } from "../components/pipeline/deal-dialogs";
import { PipelineFilters, PipelineTabs } from "../components/pipeline/pipeline-parts";
import { SalesPeek, useSalesPeek } from "../components/peek/sales-peek";
import { holderPrefill } from "../components/peek/subject-tasks";
import { useReadsHeldOffline, useRevealLatch } from "../lib/reads";
import { compactSearch } from "./search-params";
import { validateSalesPipelineSearch, type SalesPipelineSearch } from "./pipeline-search-params";

/** How close to the board's edge a dragged pointer starts it scrolling. */
const AUTOSCROLL_EDGE_PX = 48;
const AUTOSCROLL_STEP_PX = 24;

/** The extra pages Show more read for one column. */
interface ColumnMore {
  readonly items: readonly Opportunity[];
  readonly cursor: string | null;
  readonly loading: boolean;
  readonly failed: boolean;
}

function targetOfElement(el: Element | null): DropTarget | null {
  const zone = el?.closest("[data-drop-target]");
  const value = zone?.getAttribute("data-drop-target");
  if (value === undefined || value === null) return null;
  if (value === "won") return { kind: "won" };
  if (value === "lost") return { kind: "lost" };
  return value.startsWith("stage:") ? { kind: "stage", stageId: value.slice(6) } : null;
}

export function SalesPipelineBoardScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const onboardingPerms = useOnboardingPermissions();
  const desktop = useDesktop();
  const peek = useSalesPeek();

  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = useMemo(() => validateSalesPipelineSearch(rawSearch), [rawSearch]);
  const writeSearch = useCallback(
    (next: SalesPipelineSearch) => {
      void navigate({ to: "/sales/pipeline", search: compactSearch(next), replace: true });
    },
    [navigate],
  );

  const refused = isSalesReadRefused(perms, perms.canOpenPipeline);
  const canWrite = canWriteOpportunities(perms);
  const canTask = perms.has("sales.activity.write");
  const owners = useOwnerIndex(perms.isAdmin);
  // A directory read held offline reports pending until the network returns;
  // it must not hold the board's reveal for the whole cut (design-system §3.9).
  // The reveal latches: when the network returns, or a failed directory read
  // is asked again, the directory is pending once more, and the columns and
  // filters already painted stay (lib/reads.ts `useRevealLatch`).
  const offline = useReadsHeldOffline();
  const ownersSettled = useRevealLatch(!perms.isAdmin || !owners.isPending || offline);

  const query = useMemo(
    () =>
      definedOnly({
        owner_user_id: search.owner,
        customer_id: search.customer,
      }),
    [search.owner, search.customer],
  );
  const board = useOpportunityBoard(query, !refused);
  const move = useMoveOpportunity();
  const win = useWinOpportunity();
  const lose = useLoseOpportunity();

  useEffect(() => {
    if (board.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [board.error, navigate]);

  const stages: PipelineStage[] = useMemo(
    () => (board.data?.stages ?? []).map((column) => column.stage),
    [board.data],
  );
  const nameOf = useCallback(
    (id: string) => {
      const stage = stages.find((s) => s.id === id);
      return stage === undefined ? undefined : stageName(stage, i18n.language);
    },
    [stages, i18n.language],
  );

  // ---- Show more ---------------------------------------------------------
  // The pages a reader loaded stay across a refetch of the same board (a
  // window regaining focus, a move settling): only another filter starts the
  // columns again. A card a refetch now serves is shown where the server puts
  // it, never twice, and a card that moved leaves the page it was loaded on.
  const [more, setMore] = useState<Readonly<Record<string, ColumnMore>>>({});
  const boardKey = JSON.stringify(query);
  useEffect(() => {
    setMore({});
  }, [boardKey]);
  const servedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const column of board.data?.stages ?? [])
      for (const card of column.items) ids.add(card.id);
    return ids;
  }, [board.data]);
  const dropFromMore = (cardId: string) => {
    setMore((all) => {
      let changed = false;
      const next: Record<string, ColumnMore> = {};
      for (const [stageId, extra] of Object.entries(all)) {
        const items = extra.items.filter((item) => item.id !== cardId);
        if (items.length !== extra.items.length) changed = true;
        next[stageId] = { ...extra, items };
      }
      return changed ? next : all;
    });
  };
  const showMore = (column: OpportunityBoardColumn) => {
    const prior = more[column.stage.id];
    const cursor = prior?.cursor ?? column.next_cursor;
    if (cursor === null) return;
    setMore((all) => ({
      ...all,
      [column.stage.id]: {
        items: prior?.items ?? [],
        cursor,
        loading: true,
        failed: false,
      },
    }));
    fetchOpportunitiesPage(
      {
        status: "open",
        stage_id: column.stage.id,
        limit: BOARD_PAGE_SIZE,
        ...query,
      },
      cursor,
    ).then(
      (page) => {
        setMore((all) => ({
          ...all,
          [column.stage.id]: {
            items: [...(all[column.stage.id]?.items ?? []), ...page.items],
            cursor: page.next_cursor,
            loading: false,
            failed: false,
          },
        }));
      },
      () => {
        setMore((all) => ({
          ...all,
          [column.stage.id]: {
            items: all[column.stage.id]?.items ?? [],
            cursor,
            loading: false,
            failed: true,
          },
        }));
      },
    );
  };

  // ---- the moves ---------------------------------------------------------
  const [state, dispatch] = useReducer(boardReducer, INITIAL_BOARD_STATE);
  const [announce, setAnnounce] = useState("");
  const [moving, setMoving] = useState<Opportunity | null>(null);
  const [scheduling, setScheduling] = useState<Opportunity | null>(null);

  const columns = useMemo(() => {
    const served = (board.data?.stages ?? []).map((column) => ({
      stageId: column.stage.id,
      items: [
        ...column.items,
        ...(more[column.stage.id]?.items ?? []).filter((item) => !servedIds.has(item.id)),
      ],
    }));
    return placeCards(served, state.pending);
  }, [board.data, more, servedIds, state.pending]);

  const cardById = useMemo(() => {
    const map = new Map<string, Opportunity>();
    // The served board wins over a page loaded before the last refetch.
    for (const extra of Object.values(more)) for (const card of extra.items) map.set(card.id, card);
    for (const column of board.data?.stages ?? []) {
      for (const card of column.items) map.set(card.id, card);
    }
    return map;
  }, [board.data, more]);

  const targets: DropTarget[] = useMemo(
    () => [
      ...stages.map((stage): DropTarget => ({ kind: "stage", stageId: stage.id })),
      { kind: "won" },
      { kind: "lost" },
    ],
    [stages],
  );

  const targetWords = useCallback(
    (target: DropTarget | null): string => {
      if (target === null) return "";
      if (target.kind === "won") return t("sales.pipeline.board.liveOverWon");
      if (target.kind === "lost") return t("sales.pipeline.board.liveOverLost");
      return t("sales.pipeline.board.liveOver", { stage: nameOf(target.stageId) ?? "" });
    },
    [nameOf, t],
  );

  // Send the pending move once. A ref, because StrictMode runs the effect
  // twice and a second send would be a second request (the same key would
  // replay it, but a replay is still a wasted round trip).
  const sent = useRef<PendingMove | null>(null);
  useEffect(() => {
    const pending = state.pending;
    if (pending === null || sent.current === pending) return;
    sent.current = pending;
    const card = cardById.get(pending.origin.cardId);
    const title = card?.title ?? "";
    const target = pending.target;
    const request =
      target.kind === "stage"
        ? move.mutateAsync({
            id: pending.origin.cardId,
            body: { stage_id: target.stageId, from_stage_id: pending.origin.stageId },
            idempotencyKey: pending.idempotencyKey,
          })
        : target.kind === "won"
          ? win.mutateAsync({
              id: pending.origin.cardId,
              body: {},
              idempotencyKey: pending.idempotencyKey,
            })
          : lose.mutateAsync({
              id: pending.origin.cardId,
              body: {
                reason_code: pending.lose?.reason ?? "other",
                ...(pending.lose?.note !== undefined ? { note: pending.lose.note } : {}),
              },
              idempotencyKey: pending.idempotencyKey,
            });
    request.then(
      () => {
        dropFromMore(pending.origin.cardId);
        dispatch({ type: "settled" });
        setAnnounce(
          target.kind === "stage"
            ? t("sales.pipeline.board.liveDropped", {
                title,
                stage: nameOf(target.stageId) ?? "",
              })
            : target.kind === "won"
              ? t("sales.pipeline.board.liveWon", { title })
              : t("sales.pipeline.board.liveLost", { title }),
        );
      },
      (error: unknown) => {
        if (error instanceof UnauthorizedError) {
          void navigate({ to: "/login" });
          return;
        }
        if (isNetworkFailure(error)) dispatch({ type: "networkFailed" });
        else dispatch({ type: "refused", error });
      },
    );
    // The pending move is the one thing that decides whether a request goes;
    // the mutations and the words it reads are the render's own.
  }, [state.pending]);

  const originOf = (card: Opportunity): CardOrigin | undefined => {
    for (const column of columns) {
      const index = column.items.findIndex((item) => item.id === card.id);
      if (index >= 0) return { cardId: card.id, stageId: column.stageId, index };
    }
    return undefined;
  };

  // ---- pointer drag ------------------------------------------------------
  const scroller = useRef<HTMLDivElement>(null);
  const ghost = useRef<HTMLDivElement>(null);
  const pointer = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const justDragged = useRef(false);
  const drag = state.drag;

  useEffect(() => {
    if (drag.phase === "idle") return undefined;
    if (drag.phase === "dragging" && drag.via === "keyboard") return undefined;
    const onMove = (event: globalThis.PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY };
      dispatch({
        type: "pointerMove",
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      });
      if (ghost.current !== null) {
        ghost.current.style.transform = `translate3d(${event.clientX + 8}px, ${event.clientY + 8}px, 0)`;
      }
      const hit =
        typeof document.elementFromPoint === "function"
          ? document.elementFromPoint(event.clientX, event.clientY)
          : null;
      dispatch({ type: "hover", over: targetOfElement(hit) });
      const box = scroller.current?.getBoundingClientRect();
      if (box !== undefined && scroller.current !== null) {
        if (event.clientX < box.left + AUTOSCROLL_EDGE_PX)
          scroller.current.scrollLeft -= AUTOSCROLL_STEP_PX;
        else if (event.clientX > box.right - AUTOSCROLL_EDGE_PX)
          scroller.current.scrollLeft += AUTOSCROLL_STEP_PX;
      }
    };
    const onUp = () => {
      if (drag.phase === "dragging") {
        justDragged.current = true;
        // The drag's own click, if it comes, is dispatched in this same task;
        // after it, the flag has nothing left to catch (the header).
        window.setTimeout(() => {
          justDragged.current = false;
        }, 0);
        dispatch({ type: "drop", idempotencyKey: newIdempotencyKey() });
      } else {
        dispatch({ type: "cancel" });
      }
    };
    const onCancel = () => {
      dispatch({ type: "cancel" });
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
    };
  }, [drag]);

  // Escape cancels any drag, before the side peek hears it: the capture phase
  // runs ahead of every bubble listener, whenever it was added (the header).
  useEffect(() => {
    if (drag.phase !== "dragging") return undefined;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      const card = cardById.get(drag.origin.cardId);
      dispatch({ type: "cancel" });
      setAnnounce(
        t("sales.pipeline.board.liveCancelled", {
          title: card?.title ?? "",
          stage: nameOf(drag.origin.stageId) ?? "",
        }),
      );
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
    };
  }, [drag, cardById, nameOf, t]);

  // A press that may become the board's drag keeps the pointer stream until
  // the button comes up: no text starts selecting under it and the browser
  // begins no drag of its own (the header). Set in the press's own handler,
  // so it is in force before the browser's default action for that press,
  // and ended by the release in the capture phase, whatever the drag did.
  const pressHeld = useRef(false);
  useEffect(() => {
    const refuse = (event: Event) => {
      if (pressHeld.current) event.preventDefault();
    };
    const release = () => {
      pressHeld.current = false;
    };
    document.addEventListener("selectstart", refuse, true);
    document.addEventListener("dragstart", refuse, true);
    // Every press starts unheld; a card's own handler holds it, after this.
    document.addEventListener("pointerdown", release, true);
    document.addEventListener("pointerup", release, true);
    document.addEventListener("pointercancel", release, true);
    return () => {
      document.removeEventListener("selectstart", refuse, true);
      document.removeEventListener("dragstart", refuse, true);
      document.removeEventListener("pointerdown", release, true);
      document.removeEventListener("pointerup", release, true);
      document.removeEventListener("pointercancel", release, true);
    };
  }, []);

  const onCardPointerDown = (card: Opportunity, event: PointerEvent<HTMLLIElement>) => {
    // A touch never drags, and a press on the card's own controls stays a
    // click (spec 09 §4.14).
    if (event.pointerType === "touch" || event.button !== 0) return;
    if (!canWrite || !card.mutable || isInteractive(event.target)) return;
    const origin = originOf(card);
    if (origin === undefined) return;
    pressHeld.current = true;
    // Text an earlier drag left selected would make this press the browser's
    // drag of that text instead of the board's drag of the card.
    document.getSelection()?.removeAllRanges();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A synthetic event in a test has no active pointer to capture.
    }
    pointer.current = { x: event.clientX, y: event.clientY };
    dispatch({
      type: "press",
      origin,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    });
  };

  // ---- keyboard ----------------------------------------------------------
  /** A move is saving or asking for its reason; no other may start. */
  const moveBusy = state.pending !== null || state.awaitingReason !== null;
  const [focusId, setFocusId] = useState<string | undefined>(undefined);
  const focusCard = (id: string | undefined) => {
    if (id === undefined) return;
    setFocusId(id);
    const el = document.querySelector<HTMLElement>(
      `[data-testid="sales-board-card"][data-deal-id="${id}"]`,
    );
    el?.focus();
  };

  const onCardKeyDown = (card: Opportunity, event: KeyboardEvent<HTMLLIElement>) => {
    if (event.target !== event.currentTarget) return;
    const dragging = drag.phase === "dragging" && drag.origin.cardId === card.id;
    if (dragging) {
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        const next = nextTargetAnnouncement(1);
        setAnnounce(next);
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        setAnnounce(nextTargetAnnouncement(-1));
      } else if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        dispatch({ type: "drop", idempotencyKey: newIdempotencyKey() });
      }
      return;
    }
    if (event.key === " ") {
      event.preventDefault();
      // One move at a time (lib/board-state.ts): while one is saving or
      // waiting for its reason, a pick-up would be dropped by the reducer, so
      // it is not announced either.
      if (!canWrite || !card.mutable || moveBusy) return;
      const origin = originOf(card);
      if (origin === undefined) return;
      dispatch({ type: "pickUp", origin });
      setAnnounce(
        t("sales.pipeline.board.livePicked", {
          title: card.title,
          stage: nameOf(origin.stageId) ?? "",
        }),
      );
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      void navigate({
        to: "/sales/opportunities/$opportunityId",
        params: { opportunityId: card.id },
      });
      return;
    }
    const origin = originOf(card);
    if (origin === undefined) return;
    const columnIndex = columns.findIndex((column) => column.stageId === origin.stageId);
    const column = columns[columnIndex];
    if (column === undefined) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const next = column.items[origin.index + (event.key === "ArrowDown" ? 1 : -1)];
      focusCard(next?.id);
    } else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const step = event.key === "ArrowRight" ? 1 : -1;
      for (let i = columnIndex + step; i >= 0 && i < columns.length; i += step) {
        const first = columns[i]?.items[0];
        if (first !== undefined) {
          focusCard(first.id);
          break;
        }
      }
    }
  };

  const nextTargetAnnouncement = (delta: 1 | -1): string => {
    if (drag.phase !== "dragging") return "";
    const at =
      drag.over === null ? -1 : targets.findIndex((target) => sameTarget(target, drag.over));
    const next =
      at < 0
        ? delta === 1
          ? targets[0]
          : targets[targets.length - 1]
        : targets[(at + delta + targets.length) % targets.length];
    dispatch({ type: "step", targets, delta });
    return targetWords(next ?? null);
  };

  // ---- the address's New deal --------------------------------------------
  const [creating, setCreating] = useState(false);
  // A reader who cannot write deals never sees the form, whatever the address
  // says: a pasted or shared `?new_deal=` would otherwise open a create the
  // server refuses 403 on save.
  const newDealOpen = canWrite && !refused && (creating || search.new_deal !== undefined);
  const closeNewDeal = () => {
    setCreating(false);
    if (search.new_deal !== undefined || search.from_conversation !== undefined) {
      writeSearch({ ...search, new_deal: undefined, from_conversation: undefined });
    }
  };

  // ...and once the reader's grants are known, the keys leave the address with
  // a replace, so they do not linger in the history or in a copied link.
  const strayNewDeal =
    perms.isResolved &&
    !canWrite &&
    (search.new_deal !== undefined || search.from_conversation !== undefined);
  useEffect(() => {
    if (strayNewDeal) {
      writeSearch({ ...search, new_deal: undefined, from_conversation: undefined });
    }
    // The address is the one input; `writeSearch` is stable.
  }, [strayNewDeal]);

  // ---- phone: one stage at a time ----------------------------------------
  const [phoneStage, setPhoneStage] = useState<string | undefined>(undefined);
  const shownPhoneStage = phoneStage ?? stages[0]?.id;

  // ---- states -------------------------------------------------------------
  const paused = board.fetchStatus === "paused" && board.data === undefined;
  // The server's refusal for a reader the mirror could not judge (a tenant's
  // composed role) is the same fact as the mirror's, so it reads the same
  // sentence with no Retry, as the forecast's does (design-system §3.9).
  const serverRefused =
    board.data === undefined &&
    board.error instanceof RequestError &&
    board.error.httpStatus === 403;
  const isFiltered = search.owner !== undefined || search.customer !== undefined;
  const allEmpty = board.data?.stages.every((column) => column.open_count === 0) === true;

  const firstFocusable =
    focusId !== undefined && cardById.has(focusId)
      ? focusId
      : columns.find((column) => column.items.length > 0)?.items[0]?.id;

  const draggingCard = drag.phase === "dragging" ? cardById.get(drag.origin.cardId) : undefined;

  const alert = state.alert;
  const alertCard = alert === null ? undefined : cardById.get(alert.move.origin.cardId);

  const renderColumn = (column: OpportunityBoardColumn) => {
    const placed = columns.find((c) => c.stageId === column.stage.id);
    const items = placed?.items ?? [];
    const name = stageName(column.stage, i18n.language);
    const extra = more[column.stage.id];
    const cursor = extra === undefined ? column.next_cursor : extra.cursor;
    const over =
      drag.phase === "dragging" &&
      drag.over?.kind === "stage" &&
      drag.over.stageId === column.stage.id;
    return (
      <section
        key={column.stage.id}
        className={over ? "sales-board-column is-over" : "sales-board-column"}
        data-testid="sales-board-column"
        data-stage-id={column.stage.id}
        data-stage-code={column.stage.code}
        data-drop-target={`stage:${column.stage.id}`}
        aria-label={t("sales.pipeline.board.columnAria", { stage: name, count: column.open_count })}
      >
        <header className="sales-board-column-head">
          <h2 className="sales-board-column-name">{name}</h2>
          <span className="sales-hint num">
            {t("sales.pipeline.probability", { pct: column.stage.probability_pct })}
          </span>
          <span
            className="num"
            data-testid="sales-board-column-count"
            data-count={column.open_count}
          >
            {t("sales.pipeline.board.count", { count: column.open_count })}
          </span>
          {column.rotting_count > 0 ? (
            <span className="sales-valid-urgent num" data-testid="sales-board-column-rotting">
              {t("sales.pipeline.board.rottingCount", { count: column.rotting_count })}
            </span>
          ) : null}
          {(column.values ?? []).map((value) => (
            <span
              key={value.currency}
              className="sales-board-column-total"
              data-testid="sales-board-column-total"
              data-currency={value.currency}
              data-open={value.open_value_minor}
              data-weighted={value.weighted_value_minor}
            >
              <span className="num">
                {t("sales.pipeline.board.openTotal", {
                  amount: formatMinor(value.open_value_minor, value.currency, i18n.language),
                })}
              </span>
              <span className="num sales-hint">
                {t("sales.pipeline.board.weightedTotal", {
                  amount: formatMinor(value.weighted_value_minor, value.currency, i18n.language),
                })}
              </span>
              {value.unvalued_count > 0 ? (
                <span className="sales-hint num">
                  {t("sales.pipeline.board.unvaluedCount", { count: value.unvalued_count })}
                </span>
              ) : null}
            </span>
          ))}
        </header>
        <ul className="sales-board-cards">
          {items.length === 0 ? (
            <li
              className="sales-hint sales-board-column-empty"
              data-testid="sales-board-column-empty"
            >
              {t("sales.pipeline.board.columnEmpty")}
            </li>
          ) : (
            items.map((card) => (
              <BoardCard
                key={card.id}
                deal={card}
                pending={state.pending?.origin.cardId === card.id}
                moveBlocked={moveBusy}
                lifted={drag.phase === "dragging" && drag.origin.cardId === card.id}
                focusable={card.id === firstFocusable}
                peek={
                  peek.offered
                    ? {
                        code: peekCode("opportunity", card.id),
                        current: peek.code,
                        open: peek.open,
                        close: peek.close,
                      }
                    : undefined
                }
                canMove={canWrite && card.mutable}
                onMove={() => {
                  setMoving(card);
                }}
                onSchedule={
                  canTask && card.mutable
                    ? () => {
                        setScheduling(card);
                      }
                    : undefined
                }
                onPointerDown={(event) => {
                  onCardPointerDown(card, event);
                }}
                onKeyDown={(event) => {
                  onCardKeyDown(card, event);
                }}
                onFocus={() => {
                  setFocusId(card.id);
                }}
              />
            ))
          )}
        </ul>
        {cursor !== null ? (
          <div className="sales-board-more">
            <Button
              size="sm"
              data-testid="sales-board-more"
              busy={extra?.loading === true}
              busyLabel={t("sales.pipeline.loading")}
              onClick={() => {
                showMore(column);
              }}
            >
              {t("sales.pipeline.board.showMore")}
            </Button>
            {extra?.failed === true ? (
              <p className="sales-error-note" role="alert" data-testid="sales-board-more-failed">
                {t("sales.pipeline.board.showMoreFailed")}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>
    );
  };

  const boardBody = (() => {
    if (refused || serverRefused) {
      return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pipeline")} />;
    }
    if (paused) {
      return <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />;
    }
    if (board.data === undefined || !ownersSettled) {
      if (
        board.data === undefined &&
        board.error !== null &&
        !(board.error instanceof UnauthorizedError)
      ) {
        return (
          <ErrorState
            title={t("sales.pipeline.board.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(board.error)}
            onRetry={() => {
              void board.refetch();
            }}
          />
        );
      }
      return (
        <div
          className="sales-board sales-board-skeleton"
          role="status"
          aria-label={t("sales.pipeline.loading")}
          data-testid="sales-board-skeleton"
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="sales-board-column" key={i}>
              <Skeleton width="60%" height="1rem" />
              <Skeleton height="4.5rem" />
              <Skeleton height="4.5rem" />
            </div>
          ))}
        </div>
      );
    }
    if (board.data.stages.length === 0) {
      // D-343: the stages arrive with the industry pack update. The holder of
      // `onboarding.install_pack` gets the action; every other reader is told
      // who can take it. The server's PIPELINE_NOT_CONFIGURED names the same
      // remedy (`pipelineRemedyOf`), so the create refusal and this state agree.
      const canUpdatePack = onboardingPerms.has("onboarding.install_pack");
      return (
        <div data-testid="sales-board-no-stages">
          <EmptyState
            title={t("sales.pipeline.noStagesTitle")}
            body={
              canUpdatePack ? t("sales.pipeline.noStagesAdmin") : t("sales.pipeline.noStagesBody")
            }
          />
          {canUpdatePack ? (
            <Link
              to="/settings/industry-pack"
              className="ds-lnk"
              data-testid="sales-board-apply-pack-update"
            >
              {t("sales.pipeline.noStagesAction")}
            </Link>
          ) : null}
        </div>
      );
    }
    const data = board.data;
    return (
      <>
        {allEmpty ? (
          isFiltered ? (
            <div className="sales-board-banner" data-testid="sales-board-filtered-empty">
              <EmptyState
                title={t("sales.pipeline.board.filteredEmptyTitle")}
                body={t("sales.pipeline.board.filteredEmptyBody")}
                ctaLabel={t("sales.pipeline.clearFilters")}
                onCta={() => {
                  writeSearch({ ...search, owner: undefined, customer: undefined });
                }}
              />
            </div>
          ) : (
            <div className="sales-board-banner" data-testid="sales-board-empty">
              <EmptyState
                title={t("sales.pipeline.board.emptyTitle")}
                body={t("sales.pipeline.board.emptyBody")}
                {...(canWrite
                  ? {
                      ctaLabel: t("sales.pipeline.newDeal"),
                      onCta: () => {
                        setCreating(true);
                      },
                    }
                  : {})}
              />
            </div>
          )
        ) : null}
        {desktop ? (
          <>
            <p className="visually-hidden" id="sales-board-keyboard-hint">
              {t("sales.pipeline.board.keyboardHint")}
            </p>
            <div
              ref={scroller}
              className="sales-board"
              data-testid="sales-board"
              role="group"
              aria-label={t("sales.pipeline.board.aria")}
              aria-describedby="sales-board-keyboard-hint"
              onPointerDownCapture={() => {
                // A new press is never the click an old drag ended with.
                justDragged.current = false;
              }}
              onClickCapture={(event) => {
                // The click a pointer drag ends with is not a click on the card.
                if (justDragged.current) {
                  justDragged.current = false;
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
            >
              {data.stages.map(renderColumn)}
            </div>
          </>
        ) : (
          <div className="sales-board-phone" data-testid="sales-board-phone">
            <div
              className="sales-board-chips"
              role="group"
              aria-label={t("sales.pipeline.board.stagesAria")}
            >
              {data.stages.map((column) => (
                <button
                  key={column.stage.id}
                  type="button"
                  className="ds-viewchip"
                  aria-pressed={column.stage.id === shownPhoneStage}
                  data-testid="sales-board-stage-chip"
                  data-stage-id={column.stage.id}
                  onClick={() => {
                    setPhoneStage(column.stage.id);
                  }}
                >
                  {t("sales.pipeline.board.stageChip", {
                    stage: stageName(column.stage, i18n.language),
                    count: column.open_count,
                  })}
                </button>
              ))}
            </div>
            {data.stages.filter((column) => column.stage.id === shownPhoneStage).map(renderColumn)}
          </div>
        )}
      </>
    );
  })();

  return (
    <section className="sales-pipeline-screen" data-testid="sales-pipeline-screen">
      <PageHeader
        title={t("sales.pipeline.title")}
        actions={
          <>
            <div className="sales-toolbar">
              {canWrite && !refused ? (
                <Button
                  variant="primary"
                  data-testid="sales-pipeline-new-deal"
                  // A tenant with no stages can create no deal (409
                  // PIPELINE_NOT_CONFIGURED), and the board says why beneath.
                  disabled={board.data?.stages.length === 0}
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  {t("sales.pipeline.newDeal")}
                </Button>
              ) : null}
            </div>
          </>
        }
      />

      <PipelineTabs active="board" shared={{ owner: search.owner, customer: search.customer }} />

      {refused || !ownersSettled ? null : (
        <PipelineFilters
          owner={search.owner}
          customer={search.customer}
          onOwner={(owner) => {
            writeSearch({ ...search, owner });
          }}
          onCustomer={(customer) => {
            writeSearch({ ...search, customer });
          }}
          showOwner={perms.isAdmin}
          owners={owners.byId}
          ownersPending={owners.isPending && !offline}
          testPrefix="sales-board"
        />
      )}

      {alert !== null ? (
        alert.kind === "refused" ? (
          <MutationFailureNote
            data-testid="sales-board-alert"
            text={refusalText(t, alert.error, t("sales.pipeline.move.failed"), nameOf)}
            error={alert.error}
          />
        ) : (
          <div className="sales-error-note" role="alert" data-testid="sales-board-alert">
            {t("sales.pipeline.board.network")}{" "}
            <Button
              size="sm"
              data-testid="sales-board-retry"
              onClick={() => {
                dispatch({ type: "retry" });
              }}
            >
              {t("sales.pipeline.board.tryAgain")}
            </Button>
          </div>
        )
      ) : null}
      {alert !== null && alertCard !== undefined ? (
        <span className="visually-hidden" data-testid="sales-board-alert-card">
          {alertCard.title}
        </span>
      ) : null}

      <p className="visually-hidden" aria-live="polite" data-testid="sales-board-live">
        {announce}
      </p>

      {boardBody}

      {drag.phase === "dragging" ? (
        <div className="sales-board-zones" data-testid="sales-board-zones">
          <div
            className={drag.over?.kind === "won" ? "sales-board-zone is-over" : "sales-board-zone"}
            data-drop-target="won"
            data-testid="sales-board-zone-won"
          >
            {t("sales.pipeline.board.wonZone")}
          </div>
          <div
            className={drag.over?.kind === "lost" ? "sales-board-zone is-over" : "sales-board-zone"}
            data-drop-target="lost"
            data-testid="sales-board-zone-lost"
          >
            {t("sales.pipeline.board.lostZone")}
          </div>
        </div>
      ) : null}

      {drag.phase === "dragging" && drag.via === "pointer" && draggingCard !== undefined ? (
        <div
          ref={ghost}
          className="sales-board-ghost"
          aria-hidden="true"
          data-testid="sales-board-ghost"
          style={{
            transform: `translate3d(${pointer.current.x + 8}px, ${pointer.current.y + 8}px, 0)`,
          }}
        >
          {draggingCard.title}
        </div>
      ) : null}

      {moving !== null ? (
        <MoveDealModal
          title={moving.title}
          currentStageId={moving.stage_id}
          stages={stages}
          onClose={() => {
            setMoving(null);
          }}
          onChoose={(target) => {
            const origin = originOf(moving);
            setMoving(null);
            if (origin === undefined) return;
            if (target.kind === "lost") {
              dispatch({ type: "askReason", origin });
              return;
            }
            dispatch({
              type: "moveDirect",
              move: { origin, target, idempotencyKey: newIdempotencyKey() },
            });
          }}
        />
      ) : null}

      {state.awaitingReason !== null ? (
        <LoseDealModal
          dealId={state.awaitingReason.cardId}
          title={cardById.get(state.awaitingReason.cardId)?.title ?? ""}
          pending={false}
          error={undefined}
          onClose={() => {
            dispatch({ type: "cancel" });
          }}
          onConfirm={(choice) => {
            dispatch({
              type: "confirmLost",
              idempotencyKey: newIdempotencyKey(),
              lose: { reason: choice.reason, note: choice.note },
            });
          }}
        />
      ) : null}

      {newDealOpen && board.data !== undefined && stages.length > 0 ? (
        <NewDealModal
          lockedCustomerId={search.new_deal}
          fromConversation={search.from_conversation}
          tenantMonth={board.data.tenant_month}
          stages={stages}
          onClose={closeNewDeal}
        />
      ) : null}

      {scheduling !== null ? (
        <NewTaskModal
          prefill={holderPrefill({
            kind: "opportunity",
            id: scheduling.id,
            customerId: scheduling.customer_id,
            label: scheduling.opportunity_no,
          })}
          onClose={() => {
            setScheduling(null);
          }}
        />
      ) : null}

      <SalesPeek peek={peek} />
    </section>
  );
}
